"use client";

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Knopf } from "../../knopf";
import { Datumsfeld, Schalter, Textfeld } from "../../formular";
import { SymKalender, SymInfo, SymTipp, SymWarnung, SymWichtig } from "../lesen/lese-symbole";
import {
  HINWEIS_KLASSEN,
  HINWEIS_NAME,
  HinweisSymbol,
  LeseBild,
  LeseDatum,
  LeseHinweisfeld,
  LeseUnterseiten,
  UnterseitenZeilen,
  LeseVerweis,
  LesePerson,
  LeseCi,
  LeseWorkflowChip,
  LeseWorkflowKarte,
  type LeseHinweisTon,
  type LeseUnterseite,
} from "../lesen/lese-ansichten";

/*
 * Bearbeitbare Darstellungen der Wiki-Knoten (Paket E-W2, G-W4 Variante B): Hinweisfeld mit Tonwahl und Titel, Bild mit
 * Bildunterschrift, Datumsfeld, Unterseiten-Verzeichnis mit Überschrift. Gleiche Gestalt wie im Lesepfad (WYSIWYG), die
 * Bedienelemente kommen aus den Bausteinen (`Knopf`, `Textfeld`, `Datumsfeld`, `Schalter`); Verweis, Personen-, CI- und
 * Workflow-Chip sind die des Lesepfads. Eigenbau aus Standardklassen nach dem Zusammenbau `w4-b-bearbeiten`: Abweichung AW-064.
 *
 * Eingabefelder in einer Knotenansicht gehören nicht ProseMirror (Marker `data-editor-interaktiv`, `contentEditable={false}`):
 * der Text wird lokal gehalten und beim Verlassen des Feldes (und mit Enter) in das Dokument geschrieben, nie bei jedem
 * Tastendruck (sonst reißt der Editor den Fokus zurück, Fehler der Altansicht). Texte stehen als Text im Markup, nie als HTML.
 */

const INTERAKTIV = { "data-editor-interaktiv": "" } as const;

/** Text lokal halten; beim Verlassen zurückschreiben; Änderungen von außen (Rückgängig) übernehmen, solange das Feld nicht den Fokus hat. */
function useLokalerText(wert: string, uebernehmen: (neu: string) => void) {
  const [lokal, setLokal] = useState(wert);
  const fokus = useRef(false);
  useEffect(() => {
    if (!fokus.current) setLokal(wert);
  }, [wert]);
  return {
    value: lokal,
    onChange: (e: { target: { value: string } }) => setLokal(e.target.value),
    onFocus: () => {
      fokus.current = true;
    },
    onBlur: () => {
      fokus.current = false;
      if (lokal !== wert) uebernehmen(lokal);
    },
    onKeyDown: (e: { key: string; preventDefault: () => void; currentTarget: { blur: () => void } }) => {
      // Enter schließt das Feld (kein Zeilenumbruch, kein Weiterreichen an den Editor).
      if (e.key === "Enter") {
        e.preventDefault();
        e.currentTarget.blur();
      }
    },
  };
}

// ---------------------------------------------------------------------------------------------
// Hinweisfeld

const TOENE: readonly { ton: LeseHinweisTon; symbol: ReactNode }[] = [
  { ton: "info", symbol: <SymInfo /> },
  { ton: "ok", symbol: <SymTipp /> },
  { ton: "warn", symbol: <SymWarnung /> },
  { ton: "risk", symbol: <SymWichtig /> },
];

export function BearbeitenHinweisfeld({
  ton,
  titel,
  bearbeitbar,
  beiTon,
  beiTitel,
  children,
}: {
  ton: LeseHinweisTon;
  titel: string;
  bearbeitbar: boolean;
  beiTon: (ton: LeseHinweisTon) => void;
  beiTitel: (titel: string) => void;
  children: ReactNode;
}) {
  const feld = useLokalerText(titel, beiTitel);
  if (!bearbeitbar) {
    return (
      <LeseHinweisfeld ton={ton} titel={titel}>
        {children}
      </LeseHinweisfeld>
    );
  }
  const t = ton in HINWEIS_KLASSEN ? ton : "info";
  return (
    <>
      {/* Preline: component/alerts/alerts-soft-color-variants */}
      <div className={HINWEIS_KLASSEN[t]} role="note" data-hinweis-ton={t}>
        <span className="mt-2" contentEditable={false}>
          <HinweisSymbol ton={t} />
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2" contentEditable={false} {...INTERAKTIV}>
            <div className="min-w-0 flex-1">
              <Textfeld
                label="Titel des Hinweisfelds"
                labelVersteckt
                groesse="kompakt"
                placeholder={HINWEIS_NAME[t]}
                maxLength={200}
                autoComplete="off"
                {...feld}
              />
            </div>
            <div role="group" aria-label="Art des Hinweisfelds" className="flex shrink-0 items-center gap-x-0.5">
              {TOENE.map((e) => (
                <Knopf
                  key={e.ton}
                  nurSymbol
                  groesse="xs"
                  variante={e.ton === t ? "sekundaer" : "ghost"}
                  label={HINWEIS_NAME[e.ton]}
                  titel={HINWEIS_NAME[e.ton]}
                  symbol={e.symbol}
                  starr
                  ariaPressed={e.ton === t}
                  onClick={() => beiTon(e.ton)}
                />
              ))}
            </div>
          </div>
          {children}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Bild mit Unterschrift

export function BearbeitenBild({
  src,
  alt,
  unterschrift,
  bearbeitbar,
  ausgewaehlt,
  beiUnterschrift,
}: {
  src: string | null;
  alt: string;
  unterschrift: string;
  bearbeitbar: boolean;
  ausgewaehlt: boolean;
  beiUnterschrift: (unterschrift: string) => void;
}) {
  const feld = useLokalerText(unterschrift, beiUnterschrift);
  if (!bearbeitbar) return <LeseBild src={src} alt={alt} unterschrift={unterschrift} />;
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-file-views/profile-files-list */}
      <div className="space-y-2">
        <div data-drag-handle className={ausgewaehlt ? "rounded-xl ring-2 ring-primary-focus" : "rounded-xl"}>
          <LeseBild src={src} alt={alt} unterschrift="" />
        </div>
        <div contentEditable={false} {...INTERAKTIV}>
          <Textfeld
            label="Bildunterschrift"
            labelVersteckt
            groesse="kompakt"
            placeholder="Bildunterschrift (optional)"
            maxLength={300}
            autoComplete="off"
            {...feld}
          />
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Datum

const DATUM_CHIP =
  "inline-flex items-center gap-x-1 rounded-md border border-layer-line bg-layer px-1.5 py-0.5 text-sm text-foreground cursor-pointer hover:border-primary-focus focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus";
const DATUM_CHIP_GEWAEHLT =
  "inline-flex items-center gap-x-1 rounded-md border border-primary-focus bg-layer px-1.5 py-0.5 text-sm text-foreground cursor-pointer ring-2 ring-primary-focus focus:outline-hidden";
const DATUM_FLAECHE = "fixed top-0 start-0 z-70 w-72 max-w-full bg-popover border border-popover-line rounded-xl shadow-md p-4 space-y-3";
const ABSTAND = 8;

export function BearbeitenDatum({
  iso,
  zeit,
  text,
  bearbeitbar,
  ausgewaehlt,
  oeffnen,
  beiAenderung,
}: {
  iso: string;
  zeit: string | null;
  text: string;
  bearbeitbar: boolean;
  ausgewaehlt: boolean;
  oeffnen: boolean;
  beiAenderung: (wert: { iso: string; zeit: string | null }) => void;
}) {
  const [offen, setOffen] = useState(oeffnen && bearbeitbar);
  const chip = useRef<HTMLButtonElement>(null);
  const flaeche = useRef<HTMLDivElement>(null);

  // Platzieren vor dem ersten Zeichnen: unter dem Chip, bei zu wenig Platz darüber, nie über den Rand hinaus. Die Koordinaten
  // setzt der Baustein zur Laufzeit am Element (wie `dropdown-menue.tsx`); sie sind keine Gestaltung.
  useLayoutEffect(() => {
    if (!offen) return;
    const f = flaeche.current;
    const c = chip.current;
    if (!f || !c) return;
    const a = c.getBoundingClientRect();
    const b = f.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    let oben = a.bottom + ABSTAND;
    if (oben + b.height > vh - ABSTAND && a.top - ABSTAND - b.height > ABSTAND) oben = a.top - ABSTAND - b.height;
    const links = Math.max(ABSTAND, Math.min(a.left, vw - b.width - ABSTAND));
    f.style.left = `${Math.round(links)}px`;
    f.style.top = `${Math.round(Math.max(ABSTAND, oben))}px`;
  });

  useEffect(() => {
    if (!offen) return;
    const beiKlick = (e: PointerEvent) => {
      const ziel = e.target as Node;
      if (flaeche.current?.contains(ziel) || chip.current?.contains(ziel)) return;
      setOffen(false);
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOffen(false);
        chip.current?.focus();
      }
    };
    const beiScroll = (e: Event) => {
      if (flaeche.current && e.target instanceof Node && flaeche.current.contains(e.target)) return;
      setOffen(false);
    };
    document.addEventListener("pointerdown", beiKlick);
    document.addEventListener("keydown", beiTaste);
    document.addEventListener("scroll", beiScroll, true);
    return () => {
      document.removeEventListener("pointerdown", beiKlick);
      document.removeEventListener("keydown", beiTaste);
      document.removeEventListener("scroll", beiScroll, true);
    };
  }, [offen]);

  if (!bearbeitbar) return <LeseDatum iso={iso} zeit={zeit} text={text} />;

  return (
    <>
      {/* Preline: component/badge/badge-soft-color-variants */}
      <button
        ref={chip}
        type="button"
        className={ausgewaehlt ? DATUM_CHIP_GEWAEHLT : DATUM_CHIP}
        aria-haspopup="dialog"
        aria-expanded={offen}
        aria-label={`Datum ${text}, ändern`}
        contentEditable={false}
        {...INTERAKTIV}
        onClick={() => setOffen((o) => !o)}
      >
        <SymKalender />
        {text}
      </button>
      {offen &&
        createPortal(
          <>
            {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-profile-popovers/contact-profile-popover-card */}
            <div ref={flaeche} className={DATUM_FLAECHE} role="dialog" aria-label="Datum wählen">
              <Datumsfeld
                label="Datum"
                value={iso}
                autoFocus
                onChange={(e) => {
                  if (e.target.value) beiAenderung({ iso: e.target.value, zeit });
                }}
              />
              <Schalter
                label="Uhrzeit ergänzen"
                checked={zeit !== null}
                onChange={(e) => beiAenderung({ iso, zeit: e.target.checked ? "00:00" : null })}
              />
              {zeit !== null ? (
                <Datumsfeld
                  art="zeit"
                  label="Uhrzeit"
                  value={zeit}
                  onChange={(e) => {
                    if (e.target.value) beiAenderung({ iso, zeit: e.target.value });
                  }}
                />
              ) : null}
              <div className="flex justify-end">
                <Knopf
                  variante="primaer"
                  groesse="sm"
                  onClick={() => {
                    setOffen(false);
                    chip.current?.focus();
                  }}
                >
                  Fertig
                </Knopf>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Unterseiten-Verzeichnis

export const UNTERSEITEN_LEER_SATZ = "Noch keine Unterseiten. Neue Seiten unterhalb dieser Seite erscheinen hier automatisch.";

export function BearbeitenUnterseiten({
  ueberschrift,
  eintraege,
  bearbeitbar,
  ausgewaehlt,
  beiUeberschrift,
}: {
  ueberschrift: string;
  eintraege: LeseUnterseite[] | null;
  bearbeitbar: boolean;
  ausgewaehlt: boolean;
  beiUeberschrift: (ueberschrift: string) => void;
}) {
  const feld = useLokalerText(ueberschrift, beiUeberschrift);
  if (!bearbeitbar) return <LeseUnterseiten ueberschrift={ueberschrift} eintraege={eintraege} />;
  return (
    <>
      {/* Preline: data-display/data-display-sub-operational-activity-views/data-display-cat-list-groups/icon-action-list-with-dashed-dividers */}
      <section
        className={ausgewaehlt ? "bg-layer border border-layer-line rounded-xl ring-2 ring-primary-focus" : "bg-layer border border-layer-line rounded-xl"}
        aria-label={ueberschrift}
        aria-busy={eintraege === null}
      >
        <div className="px-4 py-3 border-b border-layer-line" contentEditable={false} {...INTERAKTIV}>
          <Textfeld
            label="Überschrift des Verzeichnisses"
            labelVersteckt
            groesse="kompakt"
            placeholder="Unterseiten"
            maxLength={200}
            autoComplete="off"
            {...feld}
          />
        </div>
        {eintraege === null ? (
          <div className="px-4 py-3 text-sm text-muted-foreground-1" contentEditable={false}>
            Lädt …
          </div>
        ) : eintraege.length === 0 ? (
          <div className="px-4 py-3 text-sm text-muted-foreground-1" contentEditable={false}>
            {UNTERSEITEN_LEER_SATZ}
          </div>
        ) : (
          <div contentEditable={false}>
            <UnterseitenZeilen eintraege={eintraege} ebene={1} />
          </div>
        )}
      </section>
    </>
  );
}

/**
 * Alle Darstellungen des Bearbeiten-Pfads in der Form, die `useHornEditor({ bearbeiten: { ansichten } })` erwartet; Verweis, Personen-,
 * CI- und Workflow-Chip und die Karte „Nur Ansicht“ sind die des Lesepfads (sie haben keine Bearbeitungsoberfläche, F9-a).
 */
export const BEARBEITEN_ANSICHTEN = {
  hinweisfeld: BearbeitenHinweisfeld,
  bild: BearbeitenBild,
  datum: BearbeitenDatum,
  unterseiten: BearbeitenUnterseiten,
  verweis: LeseVerweis,
  person: LesePerson,
  ci: LeseCi,
  workflowChip: LeseWorkflowChip,
  workflowKarte: LeseWorkflowKarte,
};
